import React, { useState, useEffect } from "react";
import { Pencil, Trash2, Plus, X, Check } from "lucide-react";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

const SubsectionAdmin = ({ module }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);

  const deleteSub = async (id) => {
    if (!window.confirm("Delete this subsection?")) return;
    await fetch(`${apiBaseURL}/subsections/${id}`, { method: "DELETE" });
    window.location.reload();
  };

  return (
    <div className="ml-4 mt-3 space-y-2">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800">Subsections</h4>
        <button
          className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium"
          onClick={() => {
            setEditingSub(null);
            setFormOpen(true);
          }}
        >
          <Plus className="w-4 h-4" />
          Add Subsection
        </button>
      </div>

      <ul className="space-y-2">
        {module.subsections.map((s, idx) => (
          <li
            key={s.id}
            className="flex justify-between items-center bg-gray-50 border rounded-md p-2 hover:bg-gray-100"
          >
            <span className="text-sm">
              <span className="font-medium">{s.order}. {s.title}</span>{" "}
              <span className="text-gray-500">({s.type})</span>
            </span>
            <div className="flex gap-2">
              <button
                className="text-yellow-600 hover:text-yellow-700"
                onClick={() => {
                  setEditingSub(s);
                  setFormOpen(true);
                }}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                className="text-red-600 hover:text-red-700"
                onClick={() => deleteSub(s.id)}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>

      {formOpen && (
        <SubsectionForm
          module={module}
          subsection={editingSub}
          onClose={() => setFormOpen(false)}
        />
      )}
    </div>
  );
};

const SubsectionForm = ({ module, subsection, onClose }) => {
  const [form, setForm] = useState(
    subsection || {
      slug: "",
      title: "",
      type: "video",
      subsection_url: "",
      order: 1,
      section: "",
    }
  );

  const [agents, setAgents] = useState([]);

  useEffect(() => {
    fetch(`${apiBaseURL}/agents`)
      .then((res) => res.json())
      .then((data) => setAgents(data))
      .catch(() => setAgents([]));
  }, []);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async () => {
    const url = subsection?.id
      ? `${apiBaseURL}/subsections/${subsection.id}`
      : `${apiBaseURL}/modules/${module.id}/subsections`;
    const method = subsection?.id ? "PUT" : "POST";

    await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[500px] space-y-4 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">
          {subsection ? "Edit Subsection" : "Create Subsection"}
        </h2>

        <input
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Slug"
          value={form.slug}
          onChange={(e) => handleChange("slug", e.target.value)}
        />

        <input
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Title"
          value={form.title}
          onChange={(e) => handleChange("title", e.target.value)}
        />

        <select
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          value={form.type}
          onChange={(e) => handleChange("type", e.target.value)}
        >
          <option value="video">Video</option>
          <option value="exercise">Exercise</option>
          <option value="ai">AI Agent</option>
          <option value="coach">Coach</option>
          <option value="widget">Widget</option>
          <option value="accountability">Accountability</option>
        </select>

        {form.type === "ai" ? (
          <select
            className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            value={form.subsection_url || ""}
            onChange={(e) => handleChange("subsection_url", e.target.value)}
          >
            <option value="">-- Select AI Agent --</option>
            {agents.map((a) => (
              <option key={a.id} value={a.name}>
                {a.name} — {a.description}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Subsection URL"
            value={form.subsection_url}
            onChange={(e) => handleChange("subsection_url", e.target.value)}
          />
        )}

        <input
          type="number"
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Order"
          value={form.order}
          onChange={(e) => handleChange("order", parseInt(e.target.value))}
        />

        <input
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Section"
          value={form.section}
          onChange={(e) => handleChange("section", e.target.value)}
        />

        <div className="flex justify-end space-x-2">
          <button
            className="flex items-center gap-1 bg-gray-400 hover:bg-gray-500 text-white px-3 py-2 rounded-md text-sm"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-md text-sm"
            onClick={handleSubmit}
          >
            <Check className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubsectionAdmin;
